import React, { useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Shield, Award, Users, TrendingUp, CheckCircle, Mail } from 'lucide-react';

const UeberUns = () => {
  useEffect(() => {
    document.title = 'Über uns - Robocat Casino | Experten für Casino-Bewertungen';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Über Robocat Casino ⚡ Unabhängige Casino-Bewertungen ✓ Experten-Team ✓ Faire Testkriterien ✓ Spielerschutz ✓ Kontakt');
    }
  }, []);

  const values = [
    {
      icon: <Shield className="w-10 h-10" />,
      title: 'Unabhängigkeit & Fairness',
      description: 'Wir sind eine vollständig unabhängige Bewertungsplattform und erhalten keine Zahlungen für positive Bewertungen. Unsere Tests basieren auf objektiven Kriterien und echten Spielerfahrungen.'
    },
    {
      icon: <Award className="w-10 h-10" />,
      title: 'Expertise & Erfahrung',
      description: 'Unser Team besteht aus Casino-Experten mit über 15 Jahren Erfahrung in der iGaming-Branche. Wir kennen die Branche von innen und wissen, worauf es bei einem guten Casino ankommt.'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Spielerschutz',
      description: 'Der Schutz unserer Leser steht an erster Stelle. Wir bewerten Casinos nach strengsten Sicherheits- und Fairness-Standards und empfehlen nur lizenzierte und regulierte Anbieter.'
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: 'Transparenz',
      description: 'Wir legen unsere Testmethodik offen und erklären genau, wie wir zu unseren Bewertungen kommen. Jedes Urteil ist nachvollziehbar und durch Fakten belegt.'
    }
  ];

  const testingCriteria = [
    {
      category: 'Lizenzierung & Sicherheit',
      points: [
        'Gültige Glücksspiellizenz von anerkannten Behörden',
        'SSL-Verschlüsselung und Datenschutzmaßnahmen',
        'Regelmäßige Sicherheitsaudits und RNG-Tests',
        'Verantwortungsvolles Spielen und Spielerschutzmaßnahmen'
      ]
    },
    {
      category: 'Spielangebot & Qualität',
      points: [
        'Anzahl und Vielfalt der verfügbaren Spiele',
        'Qualität der Spieleanbieter und Software',
        'RTP-Werte und Gewinnchancen',
        'Live-Casino und mobile Optimierung'
      ]
    },
    {
      category: 'Bonusbedingungen',
      points: [
        'Fairness der Umsatzbedingungen',
        'Transparenz der Bonusrichtlinien',
        'Vielfalt und Häufigkeit von Aktionen',
        'Wert für Spieler im Vergleich zum Markt'
      ]
    },
    {
      category: 'Zahlungen & Auszahlungen',
      points: [
        'Verfügbare Zahlungsmethoden',
        'Geschwindigkeit der Auszahlungen',
        'Gebührenstruktur und Limits',
        'Verifizierungsprozess'
      ]
    },
    {
      category: 'Kundensupport',
      points: [
        'Verfügbarkeit und Erreichbarkeit',
        'Deutschsprachiger Support',
        'Kompetenz und Hilfsbereitschaft',
        'Reaktionszeit bei Anfragen'
      ]
    },
    {
      category: 'Benutzererfahrung',
      points: [
        'Website-Design und Navigation',
        'Ladezeiten und Performance',
        'Mobile Optimierung',
        'Registrierungs- und Login-Prozess'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900">
      {/* Hero Section with Banner */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_seo-casino-portal-1/artifacts/bihwlqt6_1.png" 
            alt="Über Robocat Casino Banner" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-blue-950/95"></div>
        </div>
        
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Award className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Über <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Robocat Casino</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Ihre vertrauenswürdige Quelle für unabhängige Online-Casino-Bewertungen in Deutschland. Seit Jahren helfen wir Spielern, die besten und sichersten Casinos zu finden.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Unsere Mission</h2>
            <Card className="bg-slate-800/50 border-cyan-500/20 p-8">
              <p className="text-gray-300 leading-relaxed mb-4">
                Robocat Casino wurde mit einer klaren Mission gegründet: Deutschen Spielern zu helfen, sichere, faire und unterhaltsame Online-Casinos zu finden. Die iGaming-Branche wächst rasant, und mit ihr die Anzahl der Online-Casinos. Doch nicht alle Anbieter sind gleich geschaffen - während einige erstklassige Erlebnisse bieten, gibt es auch schwarze Schafe, die Spieler mit unfairen Praktiken oder schlechtem Service enttäuschen.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                Hier kommen wir ins Spiel. Als unabhängige Bewertungsplattform testen wir Online-Casinos nach objektiven Kriterien und transparenten Methoden. Unser Ziel ist es, Ihnen die Informationen zu liefern, die Sie benötigen, um informierte Entscheidungen zu treffen. Wir decken auf, was funktioniert und was nicht, loben herausragende Leistungen und warnen vor problematischen Anbietern.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Transparenz, Integrität und Spielerschutz stehen im Mittelpunkt unserer Arbeit. Wir glauben daran, dass Glücksspiel Spaß machen sollte, aber immer verantwortungsvoll und in einem sicheren Umfeld stattfinden muss. Deshalb empfehlen wir nur Casinos, die lizenziert, reguliert und auf Fairness geprüft sind.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Unsere Werte</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {values.map((value, index) => (
                <Card key={index} className="bg-slate-800/50 border-cyan-500/20 p-6 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300">
                  <div className="text-cyan-400 mb-4">{value.icon}</div>
                  <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{value.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testing Methodology */}
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Unsere Testmethodik</h2>
            <p className="text-gray-300 text-center mb-12 max-w-3xl mx-auto">
              Wir bewerten Online-Casinos nach einem umfassenden Kriterienkatalog, der alle wichtigen Aspekte des Spielerlebnisses abdeckt. Jede Kategorie wird einzeln bewertet und fließt in die Gesamtbewertung ein.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testingCriteria.map((criteria, index) => (
                <Card key={index} className="bg-slate-800/50 border-cyan-500/20 p-6">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <CheckCircle className="w-6 h-6 text-cyan-400 mr-2" />
                    {criteria.category}
                  </h3>
                  <ul className="space-y-2">
                    {criteria.points.map((point, pointIndex) => (
                      <li key={pointIndex} className="flex items-start space-x-2 text-sm text-gray-300">
                        <span className="text-cyan-400 mt-1">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Unser Experten-Team</h2>
            <Card className="bg-slate-800/50 border-cyan-500/20 p-8">
              <Users className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
              <p className="text-gray-300 leading-relaxed mb-4">
                Hinter Robocat Casino steht ein Team von erfahrenen iGaming-Profis, Casino-Testern und Branchenexperten. Jedes Mitglied unseres Teams bringt jahrelange Erfahrung in verschiedenen Bereichen der Online-Glücksspielbranche mit - von Spieleentwicklung über Casinomanagement bis hin zu Regulierung und Compliance.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Unsere Tester spielen regelmäßig in den von uns bewerteten Casinos, testen Zahlungsmethoden, kontaktieren den Kundensupport und prüfen jeden Aspekt des Spielerlebnisses. Diese praxisnahe Herangehensweise ermöglicht es uns, authentische und relevante Bewertungen zu liefern, die auf echten Erfahrungen basieren.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Kontakt</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="bg-slate-800/50 border-cyan-500/20 p-8">
                <Mail className="w-10 h-10 text-cyan-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">E-Mail-Kontakt</h3>
                <p className="text-gray-300 mb-4">
                  Haben Sie Fragen, Anregungen oder möchten Sie mit uns zusammenarbeiten? Kontaktieren Sie uns per E-Mail:
                </p>
                <a href="mailto:support@robocat.com" className="text-cyan-400 hover:text-cyan-300 transition-colors font-semibold">
                  support@robocat.com
                </a>
              </Card>
              
              <Card className="bg-slate-800/50 border-cyan-500/20 p-8">
                <Shield className="w-10 h-10 text-cyan-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Rechtliche Informationen</h3>
                <div className="text-gray-300 text-sm space-y-2">
                  <p><strong>Unternehmen:</strong> Robocat Gaming GmbH</p>
                  <p><strong>Adresse:</strong><br />Alexanderplatz 1<br />10178 Berlin<br />Deutschland</p>
                  <p><strong>Hinweis:</strong> Diese Website dient ausschließlich Informationszwecken. Wir sind kein Online-Casino.</p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default UeberUns;
